import React, { useEffect, useRef, useState } from 'react';
import { Collapse, Select, TimePicker, Input, Button, Row, Col, Upload, Spin } from 'antd';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import CopyDayModal from './CopyDayModal';
import TranslateButton from '../elements/TranslateButton/TranslateButton';
import { useTranslateFields } from '../../hooks/useTranslateFields';
import uploadStyles from '../../styles/editAnnoucement.module.scss';

export const CATEGORY_OPTIONS = [
  { value: 'medicine', label: 'Lek' },
  { value: 'supplement', label: 'Suplement' },
  { value: 'water', label: 'Voda' },
  { value: 'meal', label: 'Obrok' },
  { value: 'vitamin', label: 'Vitamin' },
  { value: 'check', label: 'Kontrola' },
  { value: 'holy', label: 'Holistička terapija' },
  { value: 'journal', label: 'Dnevnik' },
  { value: 'sport', label: 'Sport' },
  { value: 'theraphy', label: 'Terapija' },
  { value: 'tea', label: 'Čaj' },
  { value: 'tincture', label: 'Tinktura' },
  { value: 'juice', label: 'Sok' },
  { value: 'cream', label: 'Krema' },
  { value: 'api', label: 'API' },
];

export const MAX_ACTIVITY_IMAGES = 3;

const LANG_SECTIONS = [
  { key: 'sr', label: 'Srpski' },
  { key: 'hu', label: 'Mađarski' },
  { key: 'en', label: 'Engleski' },
];

// Monday-first: a protocol is always authored as one full calendar week.
// `durationDays` (7/30) only controls how many times this weekly
// template repeats when a user activates the protocol.
export const WEEKDAYS = [
  { key: 'monday', label: 'Ponedeljak' },
  { key: 'tuesday', label: 'Utorak' },
  { key: 'wednesday', label: 'Sreda' },
  { key: 'thursday', label: 'Četvrtak' },
  { key: 'friday', label: 'Petak' },
  { key: 'saturday', label: 'Subota' },
  { key: 'sunday', label: 'Nedelja' },
];

const emptyActivity = () => ({
  category: undefined,
  name: { sr: '', hu: '', en: '' },
  content: { sr: '', hu: '', en: '' },
  dosage: '',
  imageUrls: [],
  time: null,
});

// A day with no activities is simply not part of this protocol yet — days
// are optional, a protocol may cover any subset of Monday-Sunday.
const emptyDay = (dayOfWeek) => ({ dayOfWeek, activities: [] });

// The multi-language fields are nested objects, so a copy has to go one level
// deep — a spread alone would leave the duplicate sharing `name`/`content`
// with its original, and editing one would silently edit the other.
const cloneActivity = (activity) => ({
  ...activity,
  name: { ...activity.name },
  content: activity.content ? { ...activity.content } : undefined,
  // Arrays need copying for the same reason the multi-language objects do —
  // a shared reference would make the copy's gallery edit the original's.
  imageUrls: [...(activity.imageUrls || [])],
});

const weekdayOrder = (dayOfWeek) => WEEKDAYS.findIndex((w) => w.key === dayOfWeek);

// An activity's position in its day is its order (sent as `order: index + 1`).
// Setting a time re-ranks the day chronologically; the ↑/↓ buttons override
// that until the next time change. Activities without a time stay last.
export const sortActivitiesByTime = (activities) =>
  [...activities].sort((a, b) => {
    if (!a.time) return b.time ? 1 : 0;
    if (!b.time) return -1;
    return a.time.localeCompare(b.time);
  });

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

// Each activity needs its own independent upload state, so this is a
// self-contained uploader rather than a keyed state map in the parent.
//
// `onAdd` takes the base64 string rather than the finished array: selecting
// several files at once fires `beforeUpload` for each before any state
// update commits, so an array computed here from a stale `value` would drop
// all but the last. The parent appends against the latest state instead.
function ActivityImagesUpload({ value, onAdd, onRemove }) {
  const [uploadingImage, setUploadingImage] = useState(false);
  const images = value || [];

  // Reserves a slot per file as a batch arrives, so the cap is enforced
  // before the uploads that would exceed it are even read.
  const reservedCountRef = useRef(images.length);

  useEffect(() => {
    reservedCountRef.current = images.length;
  }, [images.length]);

  const handleImageUpload = async (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Dozvoljen je samo JPG, PNG ili WebP format');
      return false;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Slika je prevelika. Maksimalna veličina je 5MB');
      return false;
    }

    if (reservedCountRef.current >= MAX_ACTIVITY_IMAGES) {
      toast.error(`Aktivnost može imati najviše ${MAX_ACTIVITY_IMAGES} slike`);
      return false;
    }
    reservedCountRef.current += 1;

    setUploadingImage(true);
    try {
      onAdd(await fileToBase64(file));
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Greška pri učitavanju slike');
      reservedCountRef.current -= 1;
    } finally {
      setUploadingImage(false);
    }

    return false; // Prevent antd's default upload behavior
  };

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {images.map((image, imageIndex) => (
        <div key={imageIndex} className={uploadStyles.previewWrapper}>
          <img src={image} alt={`Slika ${imageIndex + 1}`} className={uploadStyles.imagePreview} />
          <button
            className={uploadStyles.deleteBtn}
            onClick={() => onRemove(imageIndex)}
            type="button"
            disabled={uploadingImage}
            aria-label={`Ukloni sliku ${imageIndex + 1}`}
          >
            <span className={uploadStyles.trashIcon}>🗑️</span>
          </button>
        </div>
      ))}

      {images.length < MAX_ACTIVITY_IMAGES && (
        <Upload
          accept="image/jpeg,image/jpg,image/png,image/webp"
          beforeUpload={handleImageUpload}
          showUploadList={false}
          disabled={uploadingImage}
          multiple
          className={uploadStyles.addImageWrapper}
        >
          {/* A fixed square rather than the wide dropzone: beside existing
              previews that box has nothing to say and, being flex:1, it gets
              squeezed until it stops reading as a control at all. */}
          <div className={uploadStyles.addImageTile}>
            {uploadingImage ? <Spin /> : <span className={uploadStyles.addImageIcon}>+</span>}
            <span className={uploadStyles.addImageLabel}>
              {uploadingImage
                ? 'Učitavanje...'
                : images.length === 0
                  ? 'Dodaj sliku'
                  : 'Dodaj još sliku'}
            </span>
            <small className={uploadStyles.addImageHint}>
              još {MAX_ACTIVITY_IMAGES - images.length}
            </small>
          </div>
        </Upload>
      )}
    </div>
  );
}

export default function ProtocolDaysEditor({ value, onChange }) {
  const days = value || [];
  const [copySourceDay, setCopySourceDay] = useState(null);

  const updateActivity = (dayOfWeek, activityIndex, patch) => {
    onChange(
      days.map((day) =>
        day.dayOfWeek !== dayOfWeek
          ? day
          : {
              ...day,
              activities: day.activities.map((activity, ai) =>
                ai === activityIndex ? { ...activity, ...patch } : activity,
              ),
            },
      ),
    );
  };

  const setActivityTime = (dayOfWeek, activityIndex, time) => {
    onChange(
      days.map((day) =>
        day.dayOfWeek !== dayOfWeek
          ? day
          : {
              ...day,
              activities: sortActivitiesByTime(
                day.activities.map((activity, ai) =>
                  ai === activityIndex ? { ...activity, time } : activity,
                ),
              ),
            },
      ),
    );
  };

  const moveActivity = (dayOfWeek, activityIndex, direction) => {
    const targetIndex = activityIndex + direction;
    onChange(
      days.map((day) => {
        if (day.dayOfWeek !== dayOfWeek) return day;
        if (targetIndex < 0 || targetIndex >= day.activities.length) return day;
        const activities = [...day.activities];
        [activities[activityIndex], activities[targetIndex]] = [
          activities[targetIndex],
          activities[activityIndex],
        ];
        return { ...day, activities };
      }),
    );
  };

  // Image adds go through a functional update because a batch of files
  // resolves faster than React re-renders — computing from the `days` in this
  // closure would let each append overwrite the previous one.
  const addActivityImage = (dayOfWeek, activityIndex, image) => {
    onChange((prevDays) =>
      prevDays.map((day) =>
        day.dayOfWeek !== dayOfWeek
          ? day
          : {
              ...day,
              activities: day.activities.map((activity, ai) =>
                ai !== activityIndex
                  ? activity
                  : {
                      ...activity,
                      imageUrls: [...(activity.imageUrls || []), image].slice(
                        0,
                        MAX_ACTIVITY_IMAGES,
                      ),
                    },
              ),
            },
      ),
    );
  };

  const removeActivityImage = (dayOfWeek, activityIndex, imageIndex) => {
    onChange((prevDays) =>
      prevDays.map((day) =>
        day.dayOfWeek !== dayOfWeek
          ? day
          : {
              ...day,
              activities: day.activities.map((activity, ai) =>
                ai !== activityIndex
                  ? activity
                  : {
                      ...activity,
                      imageUrls: (activity.imageUrls || []).filter(
                        (_, i) => i !== imageIndex,
                      ),
                    },
              ),
            },
      ),
    );
  };

  const updateActivityLang = (dayOfWeek, activityIndex, field, lang, val) => {
    onChange(
      days.map((day) =>
        day.dayOfWeek !== dayOfWeek
          ? day
          : {
              ...day,
              activities: day.activities.map((activity, ai) =>
                ai !== activityIndex
                  ? activity
                  : { ...activity, [field]: { ...activity[field], [lang]: val } },
              ),
            },
      ),
    );
  };

  const { translating, translate } = useTranslateFields();
  const [translatingKey, setTranslatingKey] = useState(null);

  // Single onChange call via the functional-updater form (see addActivityImage
  // above) applying both name{hu,en} and content{hu,en} together — calling
  // updateActivityLang 4 times in a row for hu/en x name/content would each
  // compute from the same stale `days` closure and the last call would win,
  // discarding the other 3.
  const translateActivity = async (dayOfWeek, activityIndex) => {
    const activity = days.find((day) => day.dayOfWeek === dayOfWeek)?.activities[activityIndex];
    if (!activity?.name?.sr?.trim()) {
      toast.error('Popunite naziv aktivnosti na srpskom pre prevođenja');
      return;
    }

    const fields = { name: activity.name.sr };
    if (activity.content?.sr?.trim()) {
      fields.content = activity.content.sr;
    }

    setTranslatingKey(`${dayOfWeek}-${activityIndex}`);
    const result = await translate(fields);
    setTranslatingKey(null);
    if (!result) return;

    onChange((prevDays) =>
      prevDays.map((day) =>
        day.dayOfWeek !== dayOfWeek
          ? day
          : {
              ...day,
              activities: day.activities.map((act, ai) =>
                ai !== activityIndex
                  ? act
                  : {
                      ...act,
                      name: { ...act.name, hu: result.hu.name, en: result.en.name },
                      ...(result.hu.content !== undefined || result.en.content !== undefined
                        ? {
                            content: {
                              ...act.content,
                              ...(result.hu.content !== undefined ? { hu: result.hu.content } : {}),
                              ...(result.en.content !== undefined ? { en: result.en.content } : {}),
                            },
                          }
                        : {}),
                    },
              ),
            },
      ),
    );

    toast.success('Prevod je uspešno primenjen');
  };

  const addActivity = (dayOfWeek) => {
    const exists = days.some((day) => day.dayOfWeek === dayOfWeek);
    if (exists) {
      onChange(
        days.map((day) =>
          day.dayOfWeek === dayOfWeek
            ? { ...day, activities: [...day.activities, emptyActivity()] }
            : day,
        ),
      );
    } else {
      onChange([...days, { dayOfWeek, activities: [emptyActivity()] }]);
    }
  };

  // The copy lands directly under its original rather than at the end of the
  // day, so the pair stays side by side while the user edits the time or
  // dosage that made them duplicate it in the first place.
  const duplicateActivity = (dayOfWeek, activityIndex) => {
    onChange(
      days.map((day) =>
        day.dayOfWeek !== dayOfWeek
          ? day
          : {
              ...day,
              activities: [
                ...day.activities.slice(0, activityIndex + 1),
                cloneActivity(day.activities[activityIndex]),
                ...day.activities.slice(activityIndex + 1),
              ],
            },
      ),
    );
    toast.success('Aktivnost kopirana');
  };

  const copyDayTo = (sourceDayOfWeek, targetKeys, mode) => {
    const source = days.find((day) => day.dayOfWeek === sourceDayOfWeek);
    if (!source) return;

    // Every target gets its own set of clones — sharing one set would make an
    // edit in Tuesday show up in Wednesday too.
    const cloneAll = () => source.activities.map(cloneActivity);

    const updated = days.map((day) =>
      !targetKeys.includes(day.dayOfWeek)
        ? day
        : {
            ...day,
            activities:
              mode === 'replace' ? cloneAll() : [...day.activities, ...cloneAll()],
          },
    );

    // Targets that aren't in `days` yet are days the protocol didn't cover.
    const created = targetKeys
      .filter((key) => !days.some((day) => day.dayOfWeek === key))
      .map((key) => ({ dayOfWeek: key, activities: cloneAll() }));

    onChange(
      [...updated, ...created].sort(
        (a, b) => weekdayOrder(a.dayOfWeek) - weekdayOrder(b.dayOfWeek),
      ),
    );
    setCopySourceDay(null);
    toast.success(
      targetKeys.length === 1 ? 'Dan kopiran' : `Dan kopiran u ${targetKeys.length} dana`,
    );
  };

  const removeActivity = (dayOfWeek, activityIndex) => {
    onChange(
      days
        .map((day) =>
          day.dayOfWeek === dayOfWeek
            ? { ...day, activities: day.activities.filter((_, ai) => ai !== activityIndex) }
            : day,
        )
        // A day with zero activities left is no longer part of the protocol.
        .filter((day) => day.activities.length > 0),
    );
  };

  const items = WEEKDAYS.map(({ key: dayOfWeek, label: weekdayLabel }) => {
    const day = days.find((d) => d.dayOfWeek === dayOfWeek) || emptyDay(dayOfWeek);

    return {
      key: dayOfWeek,
      label: `${weekdayLabel} (${day.activities.length} aktivnosti)`,
      extra: day.activities.length > 0 && (
        <Button
          type="text"
          size="small"
          // Without this the click also toggles the collapse panel it sits on.
          onClick={(event) => {
            event.stopPropagation();
            setCopySourceDay(dayOfWeek);
          }}
          icon={<img src="/assets/icons/copy.svg" alt="" style={{ width: 16, height: 16 }} />}
        >
          Kopiraj dan
        </Button>
      ),
      children: (
        <div>
          {day.activities.length === 0 ? (
            <p style={{ color: '#888', marginBottom: 12 }}>
              Nema definisanih aktivnosti za ovaj dan.
            </p>
          ) : (
            <p style={{ color: '#888', marginBottom: 12 }}>
              Aktivnosti se prikazuju ovim redosledom. Unos vremena ih automatski ređa po
              vremenu; strelicama ↑/↓ redosled možete promeniti ručno.
            </p>
          )}
          {day.activities.map((activity, activityIndex) => (
            <div
              key={activityIndex}
              style={{
                border: '1px solid #eee',
                borderRadius: 8,
                padding: 16,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12,
                }}
              >
                <strong>#{activityIndex + 1}</strong>
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button
                    size="small"
                    disabled={activityIndex === 0}
                    onClick={() => moveActivity(dayOfWeek, activityIndex, -1)}
                    aria-label="Pomeri gore"
                  >
                    ↑
                  </Button>
                  <Button
                    size="small"
                    disabled={activityIndex === day.activities.length - 1}
                    onClick={() => moveActivity(dayOfWeek, activityIndex, 1)}
                    aria-label="Pomeri dole"
                  >
                    ↓
                  </Button>
                </div>
              </div>
              <Row gutter={16}>
                <Col span={8}>
                  <label>Kategorija</label>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Izaberite kategoriju"
                    value={activity.category}
                    options={CATEGORY_OPTIONS}
                    onChange={(val) =>
                      updateActivity(dayOfWeek, activityIndex, { category: val })
                    }
                  />
                </Col>
                <Col span={8}>
                  <label>Vreme</label>
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    value={activity.time ? dayjs(activity.time, 'HH:mm') : null}
                    onChange={(val) =>
                      setActivityTime(dayOfWeek, activityIndex, val ? val.format('HH:mm') : null)
                    }
                  />
                </Col>
                <Col span={8}>
                  <label>Doziranje (opciono)</label>
                  <Input
                    value={activity.dosage}
                    onChange={(e) =>
                      updateActivity(dayOfWeek, activityIndex, { dosage: e.target.value })
                    }
                  />
                </Col>
              </Row>

              <Row gutter={16} style={{ marginTop: 12 }}>
                <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                  <TranslateButton
                    onClick={() => translateActivity(dayOfWeek, activityIndex)}
                    loading={translating && translatingKey === `${dayOfWeek}-${activityIndex}`}
                    disabled={translating && translatingKey !== `${dayOfWeek}-${activityIndex}`}
                    size="small"
                  >
                    Prevedi
                  </TranslateButton>
                </Col>
                <Col span={12}>
                  <label>Naziv aktivnosti</label>
                  {LANG_SECTIONS.map(({ key, label }) => (
                    <Input
                      key={key}
                      placeholder={`Naziv — ${label}`}
                      value={activity.name?.[key]}
                      onChange={(e) =>
                        updateActivityLang(dayOfWeek, activityIndex, 'name', key, e.target.value)
                      }
                      style={{ marginBottom: 8 }}
                    />
                  ))}
                </Col>
                <Col span={12}>
                  <label>Sadržaj podsetnika (opciono)</label>
                  {LANG_SECTIONS.map(({ key, label }) => (
                    <Input
                      key={key}
                      placeholder={`Sadržaj — ${label}`}
                      value={activity.content?.[key]}
                      onChange={(e) =>
                        updateActivityLang(
                          dayOfWeek,
                          activityIndex,
                          'content',
                          key,
                          e.target.value,
                        )
                      }
                      style={{ marginBottom: 8 }}
                    />
                  ))}
                </Col>
              </Row>

              <Row gutter={16} style={{ marginTop: 12, marginBottom: 16 }}>
                <Col span={24}>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    Slike (do {MAX_ACTIVITY_IMAGES})
                  </label>
                  <ActivityImagesUpload
                    value={activity.imageUrls}
                    onAdd={(image) => addActivityImage(dayOfWeek, activityIndex, image)}
                    onRemove={(imageIndex) =>
                      removeActivityImage(dayOfWeek, activityIndex, imageIndex)
                    }
                  />
                </Col>
              </Row>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button
                  type="text"
                  onClick={() => duplicateActivity(dayOfWeek, activityIndex)}
                  icon={
                    <img
                      src="/assets/icons/copy.svg"
                      alt=""
                      style={{ width: 16, height: 16 }}
                    />
                  }
                >
                  Kopiraj aktivnost
                </Button>
                <Button
                  type="text"
                  danger
                  onClick={() => removeActivity(dayOfWeek, activityIndex)}
                  icon={
                    <img
                      src="/assets/icons/delete.svg"
                      alt="Ukloni"
                      style={{ width: 18, height: 18 }}
                    />
                  }
                  aria-label="Ukloni aktivnost"
                />
              </div>
            </div>
          ))}
          <Button className="btn-tertiary" onClick={() => addActivity(dayOfWeek)}>
            Dodaj aktivnost
          </Button>
        </div>
      ),
    };
  });

  const copyTargets = WEEKDAYS.filter(({ key }) => key !== copySourceDay).map(
    ({ key, label }) => ({
      key,
      label,
      activityCount: days.find((day) => day.dayOfWeek === key)?.activities.length || 0,
    }),
  );

  return (
    <>
      <Collapse items={items} defaultActiveKey={['monday']} />

      <CopyDayModal
        open={!!copySourceDay}
        sourceLabel={WEEKDAYS.find((w) => w.key === copySourceDay)?.label}
        targets={copyTargets}
        onCancel={() => setCopySourceDay(null)}
        onConfirm={(targetKeys, mode) => copyDayTo(copySourceDay, targetKeys, mode)}
      />
    </>
  );
}
